import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiSuccess,
  CreateSupplierInput,
  SupplierListItem,
  SupplierListQuery,
  SupplierNeighborQuery,
  SupplierView,
  UpdateSupplierInput,
} from '@app/shared';
import { apiClient } from '../../lib/api-client';

const KEY = ['suppliers'] as const;

export function useSuppliers(query: Partial<SupplierListQuery>) {
  return useQuery({
    queryKey: [...KEY, 'list', query],
    queryFn: async (): Promise<{
      data: SupplierListItem[];
      meta: { total: number; page: number; pageSize: number };
    }> => {
      const res = await apiClient.get<{
        data: SupplierListItem[];
        meta: { total: number; page: number; pageSize: number };
      }>('/suppliers', { params: query });
      return res.data;
    },
  });
}

export function useSupplier(id: number | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    enabled: id != null,
    queryFn: async (): Promise<SupplierView> => {
      const res = await apiClient.get<ApiSuccess<SupplierView>>(`/suppliers/${id}`);
      return res.data.data;
    },
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSupplierInput): Promise<SupplierView> => {
      const res = await apiClient.post<ApiSuccess<SupplierView>>('/suppliers', input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateSupplier(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateSupplierInput): Promise<SupplierView> => {
      const res = await apiClient.patch<ApiSuccess<SupplierView>>(`/suppliers/${id}`, input);
      return res.data.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/suppliers/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** REQ-20260522 Phase 1B-Console:取上一筆 / 下一筆 */
export function useSupplierNeighbor(
  currentId: number | undefined,
  query: SupplierNeighborQuery,
) {
  return useQuery({
    queryKey: [...KEY, 'neighbor', currentId, query],
    enabled: currentId != null && Number.isFinite(currentId),
    queryFn: async (): Promise<{ id: number } | null> => {
      const res = await apiClient.get<ApiSuccess<{ id: number } | null>>(
        `/suppliers/${currentId}/neighbor`,
        { params: query },
      );
      return res.data.data;
    },
  });
}
